// components/admin/ContactMessagesManager.js
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const ContactMessagesManager = ({ setMessage }) => {
    const {
        getContactMessages,
        getContactMessage,
        updateContactMessageStatus,
        replyToContactMessage,
        deleteContactMessage,
        getContactMessagesStats
    } = useAdmin();

    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        search: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    });
    const [replyText, setReplyText] = useState('');
    const [showReplyModal, setShowReplyModal] = useState(false);

    useEffect(() => {
        loadMessages();
        loadStats();
    }, [filters]);

    const loadMessages = async () => {
        try {
            setLoading(true);
            const data = await getContactMessages(filters);
            setMessages(data.messages || []);
            setPagination(data.pagination || {
                page: 1,
                limit: 10,
                total: 0,
                pages: 1
            });
        } catch (error) {
            console.error('Failed to load messages:', error);
            setMessage({ type: 'error', text: 'Failed to load messages' });
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await getContactMessagesStats();
            setStats(data.stats);
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const handleStatusChange = async (messageId, newStatus) => {
        try {
            await updateContactMessageStatus(messageId, newStatus);
            setMessage({ type: 'success', text: 'Status updated successfully' });
            loadMessages();
            loadStats();
        } catch (error) {
            console.error('Failed to update status:', error);
            setMessage({ type: 'error', text: 'Failed to update status' });
        }
    };

    const handleDelete = async (messageId) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
            try {
                await deleteContactMessage(messageId);
                setMessage({ type: 'success', text: 'Message deleted successfully' });
                loadMessages();
                loadStats();
            } catch (error) {
                console.error('Failed to delete message:', error);
                setMessage({ type: 'error', text: 'Failed to delete message' });
            }
        }
    };

    const handleReply = async () => {
        if (!replyText.trim() || !selectedMessage) return;

        try {
            await replyToContactMessage(selectedMessage._id, replyText);
            setMessage({ type: 'success', text: 'Reply sent successfully' });
            setShowReplyModal(false);
            setReplyText('');
            loadMessages();
            loadStats();
        } catch (error) {
            console.error('Failed to send reply:', error);
            setMessage({ type: 'error', text: 'Failed to send reply' });
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewMessage = async (messageId) => {
        try {
            const data = await getContactMessage(messageId);
            setSelectedMessage(data.message);
        } catch (error) {
            console.error('Failed to load message:', error);
            setMessage({ type: 'error', text: 'Failed to load message' });
        }
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    if (loading && !messages.length) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading messages...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    <i className="fas fa-envelope text-primary me-2"></i>
                    Contact Messages Management
                </h4>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Total Messages</h5>
                                <h2>{stats.total}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body text-center">
                                <h5 className="card-title">Unread</h5>
                                <h2>{stats.unread}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Read</h5>
                                <h2>{stats.read}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Replied</h5>
                                <h2>{stats.replied}</h2>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select"
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                            >
                                <option value="all">All Status</option>
                                <option value="unread">Unread</option>
                                <option value="read">Read</option>
                                <option value="replied">Replied</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, email, or subject..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Items per page</label>
                            <select
                                className="form-select"
                                value={filters.limit}
                                onChange={(e) => setFilters({ ...filters, limit: e.target.value, page: 1 })}
                            >
                                <option value="10">10 per page</option>
                                <option value="20">20 per page</option>
                                <option value="50">50 per page</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Table */}
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Subject</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map((message) => (
                                    <tr
                                        key={message._id}
                                        className={message.status === 'unread' ? 'table-active' : ''}
                                    >
                                        <td>{message.name}</td>
                                        <td>
                                            <a href={`mailto:${message.email}`} className="text-decoration-none">
                                                {message.email}
                                            </a>
                                        </td>
                                        <td>
                                            <span
                                                className="text-primary cursor-pointer"
                                                onClick={() => handleViewMessage(message._id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {message.subject.length > 30 ? `${message.subject.substring(0, 30)}...` : message.subject}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${message.status === 'unread' ? 'bg-warning' :
                                                message.status === 'read' ? 'bg-info' :
                                                    message.status === 'replied' ? 'bg-success' :
                                                        'bg-secondary'}`}>
                                                {message.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(message.createdAt)}</td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleViewMessage(message._id)}
                                                    title="View Details"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-success"
                                                    onClick={() => {
                                                        setSelectedMessage(message);
                                                        setShowReplyModal(true);
                                                    }}
                                                    title="Reply"
                                                    disabled={message.status === 'replied'}
                                                >
                                                    <i className="fas fa-reply"></i>
                                                </button>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={message.status}
                                                    onChange={(e) => handleStatusChange(message._id, e.target.value)}
                                                    style={{ width: '120px' }}
                                                >
                                                    <option value="unread">Unread</option>
                                                    <option value="read">Read</option>
                                                    <option value="replied">Replied</option>
                                                    <option value="archived">Archive</option>
                                                </select>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDelete(message._id)}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* No messages */}
                    {messages.length === 0 && !loading && (
                        <div className="text-center py-5">
                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No messages found</h5>
                            <p className="text-muted">Try changing your filters</p>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <nav className="mt-4">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={pagination.page === 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {[...Array(pagination.pages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === pagination.pages ||
                                        (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                    ) {
                                        return (
                                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    } else if (
                                        pageNum === pagination.page - 3 ||
                                        pageNum === pagination.page + 3
                                    ) {
                                        return (
                                            <li key={pageNum} className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                    return null;
                                })}

                                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={pagination.page === pagination.pages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>

            {/* Message Details Modal */}
            {selectedMessage && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Message Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setSelectedMessage(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Name:</strong>
                                        <p className="mb-0">{selectedMessage.name}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Email:</strong>
                                        <p className="mb-0">
                                            <a href={`mailto:${selectedMessage.email}`} className="text-decoration-none">
                                                {selectedMessage.email}
                                            </a>
                                        </p>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <strong>Subject:</strong>
                                    <p className="mb-0">{selectedMessage.subject}</p>
                                </div>
                                <div className="mb-3">
                                    <strong>Message:</strong>
                                    <div className="border rounded p-3 bg-light mt-2">
                                        {selectedMessage.message}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Status:</strong>
                                        <p className="mb-0">
                                            <span className={`badge ${selectedMessage.status === 'unread' ? 'bg-warning' :
                                                selectedMessage.status === 'read' ? 'bg-info' :
                                                    selectedMessage.status === 'replied' ? 'bg-success' :
                                                        'bg-secondary'}`}>
                                                {selectedMessage.status}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Received:</strong>
                                        <p className="mb-0">{formatDate(selectedMessage.createdAt)}</p>
                                    </div>
                                </div>
                                {selectedMessage.ipAddress && (
                                    <div className="mt-2">
                                        <strong>IP Address:</strong>
                                        <p className="mb-0 text-muted small">{selectedMessage.ipAddress}</p>
                                    </div>
                                )}
                                {selectedMessage.replyMessage && (
                                    <div className="mt-4">
                                        <strong>Reply Sent:</strong>
                                        <div className="border rounded p-3 bg-success bg-opacity-10 mt-2">
                                            {selectedMessage.replyMessage}
                                            <div className="text-muted small mt-2">
                                                Replied on: {formatDate(selectedMessage.repliedAt)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedMessage(null)}
                                >
                                    Close
                                </button>
                                {selectedMessage.status !== 'replied' && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setSelectedMessage(selectedMessage);
                                            setShowReplyModal(true);
                                        }}
                                    >
                                        <i className="fas fa-reply me-2"></i>
                                        Reply
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reply Modal */}
            {showReplyModal && selectedMessage && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reply to {selectedMessage.name}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowReplyModal(false);
                                        setReplyText('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Original Message:</label>
                                    <div className="border rounded p-2 bg-light mb-3">
                                        <strong>Subject:</strong> {selectedMessage.subject}<br />
                                        <strong>Message:</strong> {selectedMessage.message.substring(0, 100)}...
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Your Reply:</label>
                                    <textarea
                                        className="form-control"
                                        rows="5"
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Type your reply here..."
                                    />
                                </div>
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle me-2"></i>
                                    This will mark the message as replied and save your response.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowReplyModal(false);
                                        setReplyText('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleReply}
                                    disabled={!replyText.trim()}
                                >
                                    Send Reply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactMessagesManager;